import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Swal from "sweetalert2";
import {
  producaoService,
  ProducaoFilters,
  Producao,
} from "../../../../services/producaoService";
import { lojaService } from "../../../../services/lojaService";
import { usuarioService } from "../../../../services/usuarioService";
import { BrazilDate } from "../../../../utils/BrazilDate";
import { useAuth } from "../../../../context/AuthContext";
import { hasFullAccessUser } from "../../../../lib/screenAccess";
import { useDeleteProducao } from "../../../../hooks/useProducaoMutations";
import { COLORS } from "../../../../constants";

interface HistoricoProducaoContextData {
  producoes: Producao[];
  isLoading: boolean;
  startDate: string;
  endDate: string;
  selectedLojaId: number | null;
  selectedUsuarioId: number | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  setSelectedLojaId: (id: number | null) => void;
  setSelectedUsuarioId: (id: number | null) => void;
  setCurrentPage: (page: number) => void;
  handleApplyFilter: () => void;
  handleDelete: (rowIndex: number) => Promise<void>;
  isAdministrator: boolean;
  lojaOptions: Array<{ value: number; label: string }>;
  usuarioOptions: Array<{ value: number; label: string }>;
  formatDate: (dateString: string | null) => string;
  formatTime: (seconds: number | null) => string;
}

interface HistoricoProducaoProviderProps {
  children: React.ReactNode;
}

const HistoricoProducaoContext = createContext<HistoricoProducaoContextData>(
  {} as HistoricoProducaoContextData
);

export const HistoricoProducaoProvider: React.FC<
  HistoricoProducaoProviderProps
> = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const deleteMutation = useDeleteProducao();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedLojaId, setSelectedLojaId] = useState<number | null>(null);
  const [selectedUsuarioId, setSelectedUsuarioId] = useState<number | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [appliedFilters, setAppliedFilters] = useState<ProducaoFilters>({});
  const itemsPerPage = 10;

  const isAdministrator = hasFullAccessUser(user);

  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const { data: usuariosResponse } = useQuery({
    queryKey: ["usuarios", "options"],
    queryFn: () => usuarioService.getUsuarios({ limit: 1000 }),
  });
  const usuarios = useMemo(
    () => usuariosResponse?.data ?? [],
    [usuariosResponse?.data]
  );

  const { data: paginatedData, isLoading } = useQuery({
    queryKey: [
      "producoes-historicos",
      appliedFilters.lojaId,
      appliedFilters.usuarioId,
      currentPage,
      appliedFilters.dataInicio,
      appliedFilters.dataFim,
    ],
    queryFn: () =>
      producaoService.getAll({
        lojaId: appliedFilters.lojaId || undefined,
        usuarioId: appliedFilters.usuarioId || undefined,
        page: currentPage,
        limit: itemsPerPage,
        dataInicio: appliedFilters.dataInicio,
        dataFim: appliedFilters.dataFim,
      }),
  });

  const producoes = paginatedData?.data || [];
  const totalPages = paginatedData?.totalPages || 0;
  const totalItems = paginatedData?.total || 0;

  const lojaOptions = lojas.map((loja) => ({
    value: loja.id,
    label: loja.nome,
  }));

  const usuarioOptions = usuarios
    .filter((u) => u.role?.nome === "Pizzaiolo")
    .filter((u) => !selectedLojaId || u.lojaId === selectedLojaId)
    .map((usuario) => ({
      value: usuario.id,
      label: usuario.nome,
    }));

  useEffect(() => {
    if (selectedLojaId == null || selectedUsuarioId == null) return;
    const user = usuarios.find((u) => u.id === selectedUsuarioId);
    const isPizzaiolo = user?.role?.nome === "Pizzaiolo";
    const belongsToSelectedLoja = user && user.lojaId === selectedLojaId;
    if (!isPizzaiolo || !belongsToSelectedLoja) {
      setSelectedUsuarioId(null);
    }
  }, [selectedLojaId, selectedUsuarioId, usuarios]);

  const handleApplyFilter = () => {
    setCurrentPage(1);
    setAppliedFilters({
      dataInicio: startDate,
      dataFim: endDate,
      lojaId: selectedLojaId,
      usuarioId: selectedUsuarioId,
    });
  };

  const formatDate = (dateString: string | null) => {
    return BrazilDate.formatBRNaive(dateString, true);
  };

  const formatTime = (seconds: number | null) => {
    if (!seconds) return "-";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleDelete = async (rowIndex: number) => {
    const producao = producoes[rowIndex];
    if (!producao) return;
    const result = await Swal.fire({
      title: "Excluir registro?",
      text: "Esta ação não pode ser desfeita.",
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: "Cancelar",
      confirmButtonColor: COLORS.PRIMARY,
      confirmButtonText: "Sim, excluir!",
    });
    if (result.isConfirmed) {
      try {
        await deleteMutation.mutateAsync(producao.id);
        queryClient.invalidateQueries({ queryKey: ["producoes-historicos"] });
        await Swal.fire({
          title: "Excluído",
          text: "Registro excluído com sucesso.",
          icon: "success",
          confirmButtonColor: COLORS.PRIMARY,
        });
      } catch {
        Swal.fire({
          title: "Erro",
          text: "Não foi possível excluir o registro.",
          icon: "error",
          confirmButtonColor: COLORS.PRIMARY,
        });
      }
    }
  };

  return (
    <HistoricoProducaoContext.Provider
      value={{
        producoes,
        isLoading,
        startDate,
        endDate,
        selectedLojaId,
        selectedUsuarioId,
        currentPage,
        totalPages,
        totalItems,
        itemsPerPage,
        setStartDate,
        setEndDate,
        setSelectedLojaId,
        setSelectedUsuarioId,
        setCurrentPage,
        handleApplyFilter,
        handleDelete,
        isAdministrator,
        lojaOptions,
        usuarioOptions,
        formatDate,
        formatTime,
      }}
    >
      {children}
    </HistoricoProducaoContext.Provider>
  );
};

export const useHistoricoProducao = () => {
  const context = useContext(HistoricoProducaoContext);
  if (!context) {
    throw new Error(
      "useHistoricoProducao deve ser usado dentro de HistoricoProducaoProvider"
    );
  }
  return context;
};
